'use client';

/**
 * OutcomeStatementCard — the org's outcome statement (outcome-based pricing).
 *
 * Lists every outcome recorded for the org in a month (an enrollment, a
 * completed referral …) with the member, status and price, so the org can check
 * what it will be billed for and dispute anything wrong before it is invoiced.
 * Renders nothing when the org has no outcomes, so orgs not on outcome pricing
 * never see it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Download, Flag, Loader2, Target } from 'lucide-react';
import {
  disputeOutcome,
  downloadOutcomeStatement,
  getOutcomeStatement,
  type OutcomeStatement,
} from '@/services/api/clientBillingAPI';

const STATUS: Record<string, { label: string; cls: string }> = {
  detected: { label: 'Recorded', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  pending_verification: { label: 'Under review', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  verified: { label: 'Verified', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  billable: { label: 'To be invoiced', cls: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  invoiced: { label: 'Invoiced', cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  rejected: { label: 'Not billed', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
  reversed: { label: 'Credited', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};

const monthBounds = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return { start: `${ym}-01`, end: new Date(y, m, 0).toISOString().slice(0, 10) };
};
const thisMonth = () => new Date().toISOString().slice(0, 7);
const money = (n: number | null) =>
  n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OutcomeStatementCard({ cboId }: { cboId?: number }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState<OutcomeStatement | null>(null);
  const [anyOutcomes, setAnyOutcomes] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [disputing, setDisputing] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Does this org have any outcomes at all? (decides whether to show the card)
  useEffect(() => {
    getOutcomeStatement(cboId)
      .then((s) => setAnyOutcomes(s.rows.length > 0))
      .catch(() => setAnyOutcomes(false));
  }, [cboId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = monthBounds(month);
      setData(await getOutcomeStatement(cboId, start, end));
    } catch (e: any) {
      setError(e.message || 'Could not load outcomes');
    } finally {
      setLoading(false);
    }
  }, [cboId, month]);

  useEffect(() => {
    if (anyOutcomes) load();
  }, [anyOutcomes, load]);

  if (!anyOutcomes) return null;

  const submitDispute = async (id: number) => {
    if (!note.trim()) return;
    try {
      await disputeOutcome(id, note.trim(), cboId);
      setDisputing(null);
      setNote('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Could not submit the dispute');
    }
  };

  const rows = data?.rows || [];
  const { start, end } = monthBounds(month);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Target className="w-4 h-4" /> Outcome statement
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 max-w-2xl">
            Outcomes recorded for your organization this month and what each is billed. If something is wrong, dispute it —
            it is held off your invoice until it is reviewed.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm">
            <span className="block text-xs text-gray-500 mb-1">Month</span>
            <input
              id="outcome-statement-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </label>
          <button
            onClick={() => downloadOutcomeStatement(cboId, start, end).catch((e) => setError(e.message))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
          >
            <Download className="w-4 h-4" /> Download CSV
          </button>
        </div>
      </div>

      {data && Object.keys(data.summary).length > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.summary).map(([name, s]) => (
            <div key={name} className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm">
              <div className="font-medium text-gray-900 dark:text-gray-100">{name}</div>
              <div className="text-gray-500 dark:text-gray-400">
                {s.count} recorded · {money(s.billed)} invoiced
                {s.pending ? ` · ${s.pending} pending` : ''}
                {s.disputed ? ` · ${s.disputed} disputed` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No outcomes recorded for this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left py-2 pr-3 font-medium">Date</th>
                <th className="text-left py-2 pr-3 font-medium">Outcome</th>
                <th className="text-left py-2 pr-3 font-medium">Member</th>
                <th className="text-left py-2 pr-3 font-medium">Detail</th>
                <th className="text-left py-2 pr-3 font-medium">Status</th>
                <th className="text-right py-2 pr-3 font-medium">Price</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((r) => {
                const st = STATUS[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-600' };
                const canDispute = !r.disputed && !['rejected', 'reversed'].includes(r.status);
                return (
                  <React.Fragment key={r.outcome_id}>
                    <tr>
                      <td className="py-2 pr-3 whitespace-nowrap">{r.occurred_at ? new Date(r.occurred_at).toLocaleDateString() : '—'}</td>
                      <td className="py-2 pr-3">{r.outcome}</td>
                      <td className="py-2 pr-3">{r.member_name || (r.member_id ? `Member ${r.member_id}` : '—')}</td>
                      <td className="py-2 pr-3 text-gray-500">{r.detail}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${st.cls}`}>{st.label}</span>
                        {r.disputed && (
                          <span className="ml-1 px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">Disputed</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(r.price)}</td>
                      <td className="py-2 text-right text-xs">
                        {canDispute && disputing !== r.outcome_id && (
                          <button onClick={() => { setDisputing(r.outcome_id); setNote(''); }} className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600">
                            <Flag className="w-3 h-3" /> Dispute
                          </button>
                        )}
                      </td>
                    </tr>
                    {disputing === r.outcome_id && (
                      <tr>
                        <td colSpan={7} className="pb-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              id={`dispute-note-${r.outcome_id}`}
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="What's wrong with this outcome?"
                              className="flex-1 min-w-[16rem] px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                            />
                            <button onClick={() => submitDispute(r.outcome_id)} disabled={!note.trim()} className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:bg-gray-400">
                              Submit dispute
                            </button>
                            <button onClick={() => setDisputing(null)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
